//
// Regras de sessao do portal, sem nenhuma dependencia de Vue, DOM ou
// localStorage - para poder ser testado sozinho e para que a decisao fique
// num lugar so.
//
// O comportamento antigo era: renovar o token 5 minutos antes de expirar,
// SEMPRE, sem olhar se havia alguem do outro lado. Com a aba aberta o portal
// se renovava a cada 25 minutos ate o refresh de 24h morrer - ou seja, sessao
// de um dia inteiro sem ninguem tocar em nada. Foi a reclamacao do cliente.
//
// Agora sao tres relogios independentes, todos por timestamp absoluto:
//
//   ociosidade  - tempo desde a ultima interacao DO USUARIO
//   teto        - tempo desde o login, independente de atividade
//   token       - expiracao do access token, lida do proprio JWT
//
// A renovacao deixa de ser automatica e passa a ser CONSEQUENCIA da atividade.
// E o que atende as duas exigencias ao mesmo tempo: ocioso desloga com aviso,
// ativo nao desloga.
//

/** 15 min sem interacao do usuario encerra a sessao. */
export const LIMITE_OCIOSIDADE_MS = 15 * 60 * 1000

/** Aviso com contagem regressiva 60s antes de encerrar. */
export const AVISO_ANTES_MS = 60 * 1000

/** Teto absoluto da sessao, contado do login, mesmo com uso continuo. */
export const TETO_SESSAO_MS = 3 * 60 * 60 * 1000

/** Renova o access token quando faltar isto - e so se o usuario estiver ativo. */
export const RENOVAR_QUANDO_FALTAR_MS = 2 * 60 * 1000

/**
 * Decide o que fazer neste instante. Funcao pura: recebe timestamps em
 * milissegundos e devolve a acao.
 *
 * acao:
 *   'encerrar' - deslogar agora; `motivo` diz por que
 *   'avisar'   - mostrar o dialogo com contagem; `segundos` e o que falta
 *   'renovar'  - usuario ativo e token perto de expirar
 *   'nada'     - seguir
 *
 * `motivo` em 'avisar' e 'encerrar' e 'ociosidade' ou 'teto'. Importa para a
 * UI: no caso do teto nao adianta oferecer "Continuar conectado", porque
 * nenhuma interacao adia o teto.
 */
export function decidir({ agora, ultimaAtividade, inicioSessao, expiracaoToken }) {
    const restanteTeto = inicioSessao
        ? (inicioSessao + TETO_SESSAO_MS) - agora
        : Number.POSITIVE_INFINITY

    // Sem registro de atividade tratamos como ociosidade maxima: e o estado de
    // quem tem token no storage mas nunca passou pelo login desta versao.
    const restanteOciosidade = ultimaAtividade
        ? (ultimaAtividade + LIMITE_OCIOSIDADE_MS) - agora
        : 0

    if (restanteTeto <= 0) {
        return { acao: 'encerrar', motivo: 'teto', restanteOciosidade: 0, restanteTeto: 0 }
    }

    if (restanteOciosidade <= 0) {
        return { acao: 'encerrar', motivo: 'ociosidade', restanteOciosidade: 0, restanteTeto }
    }

    const base = { restanteOciosidade, restanteTeto }
    const menor = Math.min(restanteOciosidade, restanteTeto)

    if (menor <= AVISO_ANTES_MS) {
        return {
            ...base,
            acao: 'avisar',
            motivo: restanteTeto < restanteOciosidade ? 'teto' : 'ociosidade',
            segundos: Math.max(1, Math.ceil(menor / 1000))
        }
    }

    // Renovar so depois de descartar encerrar e avisar: nao faz sentido
    // estender o token de quem esta prestes a ser desconectado.
    if (expiracaoToken && (expiracaoToken - agora) <= RENOVAR_QUANDO_FALTAR_MS) {
        return { ...base, acao: 'renovar' }
    }

    return { ...base, acao: 'nada' }
}

/**
 * Expiracao do access token, em ms epoch, lida da claim `exp` do proprio JWT.
 *
 * Por que nao usar o dtExpTokenAcesso que o auth-server devolve: ele e um
 * LocalDateTime serializado SEM fuso ("2026-09-09T21:30:00"). O servidor roda
 * em UTC no Cloud Run e o navegador parsearia essa string como hora local -
 * num cliente em America/Sao_Paulo daria 3h de diferenca, para mais. A claim
 * `exp` do JWT e epoch em UTC por especificacao, entao nao tem ambiguidade.
 *
 * Devolve null quando o token nao e um JWT legivel; quem chama decide o
 * fallback.
 */
export function expiracaoDoToken(jwt) {
    if (!jwt || typeof jwt !== 'string') return null

    const partes = jwt.split('.')
    if (partes.length !== 3) return null

    try {
        const base64 = partes[1].replace(/-/g, '+').replace(/_/g, '/')
        const payload = JSON.parse(decodeURIComponent(
            atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            }).join('')
        ))

        return typeof payload.exp === 'number' ? payload.exp * 1000 : null
    } catch (e) {
        return null
    }
}
